import { useState, useEffect, useRef, memo } from 'react';
import { withStyles } from '@material-ui/core';
import { SyncOutlined as Refresh } from '@material-ui/icons';
import Tooltip from '../../../../../../../common/StyledToolTip';
import AssignReassignRecommendedDrivers from './AssignReassignRecommendedDrivers';
import { format, differenceInMinutes } from 'date-fns';
import appConstants from '../../../../../../../../appConstants';
import Events from '../../../../../../../../utilities/EventEmitter';
import { RECOMMENDATION_EXPIRY_TIME_IN_MINUTES } from '../constants';

const styles = (theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: '16px',
    padding: '0 10 0 10'
  },
  subContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px'
  },
  refreshContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  refreshTooltip: {
    fontSize: theme.dispatchCardTextFontSize,
    color: theme.iconColor,
    fontWeight: 600
  },
  refreshIcon: {
    fontSize: 20,
    color: theme.iconColor,
    margin: 4,
    opacity: 0.8,
    '&:hover': {
      color: theme.HeaderSelectedText
    }
  },
  refreshButton: {
    padding: 0,
    alignSelf: 'center'
  },
  refreshTime: {
    fontSize: theme.dispatchCardTextFontSize,
    fontWeight: 500,
    color: theme.iconColor,
    paddingRight: 6
  },
  iconSeparator: {
    borderRight: `1px solid ${theme.gridIconBorderColor}`
  },
  '@keyframes rotate': {
    '0%': {
      transform: 'rotate(0deg)'
    },
    '100%': {
      transform: 'rotate(360deg)'
    }
  },
  refreshAnimation: {
    animation: 'rotate 2s infinite linear',
    cursor: "not-allowed",
    opacity: 0.7,
  },
  warningStyle: {
    color: theme.error
  }
});

const getFormattedTime = (time) => {
  return time ? format(time, appConstants.DATETYPES.TIME.format) : null;
};

const RecommendationHeaderRenderer = ({ headerName, headerTooltip, classes, api, headerWidth }) => {
  const [recommendationTime, setRecommendationTime] = useState(
    api.runCommand?.('lastRefreshRecommendationTime')
  );
  const [isDataRefreshProgress, setIsDataRefreshProgress] = useState(
    api.runCommand?.('isRecommendationRefreshInProgress')
  );
  const [isRecommendationExpired, setIsRecommendationExpired] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    checkRecommendationExpiry();
    Events.on('RefreshRecommendationHeader', refreshRecommendationHeader);
    return () => {
      Events.off('RefreshRecommendationHeader', refreshRecommendationHeader);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [recommendationTime]);

  const refreshRecommendationHeader = ({ time, isDataRefreshProgress = false }) => {
    time !== undefined && setRecommendationTime(time);
    setIsDataRefreshProgress(isDataRefreshProgress);
  };

  const manualRecommendationRefreshClick = () => {
    setIsRecommendationExpired(false);
    api.runCommand?.('manualRefreshPickupRecommendations');
  };

  const checkRecommendationExpiry = () => {
    // Clear existing timer first
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (!recommendationTime) {
      return;
    }
    const checkExpiration = () => {
      const minutesDifference = differenceInMinutes(new Date(), recommendationTime);
      if (minutesDifference >= RECOMMENDATION_EXPIRY_TIME_IN_MINUTES) {
        !isRecommendationExpired && setIsRecommendationExpired(true);
      } else if (isRecommendationExpired) {
        setIsRecommendationExpired(false);
      }
    };
    
    // Execute the function immediately
    checkExpiration();
    // Set up the interval to execute the function every one minute
    timerRef.current = setInterval(checkExpiration, appConstants.ONE_MINUTE_TIME_OUT);
  };

  const getRecommendationTimeClasses = () => {
    const recommendationExpiredClass = isRecommendationExpired ? classes.warningStyle : '';
    const recommendationClass = recommendationTime
        ? `${classes.iconSeparator} ${recommendationExpiredClass}`
        : ''
    return `${classes.refreshTime} ${recommendationClass}`
  }

  return (
    <div className={classes.container} style={{ width: headerWidth }}>
      <Tooltip title={headerTooltip}>
        <span>{headerName}</span>
      </Tooltip>
      <div className={classes.subContainer}>
        <AssignReassignRecommendedDrivers runCommand={api.runCommand} />
        <div className={classes.refreshContainer}>
          <Tooltip
            placement="top"
            title={
              isRecommendationExpired ? 'Recommendation expired, please refresh' : 'Last refresh at'
            }
            disableFocusListener>
            <span
              className={getRecommendationTimeClasses()}>
              {getFormattedTime(recommendationTime) ?? ''}
            </span>
          </Tooltip>
          <Tooltip
            placement="top"
            title={isDataRefreshProgress ? 'Refreshing recommendations' : 'Refresh recommendations'}
            disableFocusListener>
            <div
              className={classes.refreshButton}
              onClick={isDataRefreshProgress ? null : manualRecommendationRefreshClick}>
              <Refresh
                className={`${classes.refreshIcon} ${
                  isDataRefreshProgress ? classes.refreshAnimation : ''
                }`}
              />
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default withStyles(styles)(memo(RecommendationHeaderRenderer));
